import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const SERVICES = [
	{
		icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 7h18M3 12h18M3 17h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
		title: 'Kitchen Remodeling',
		desc: 'Transform your kitchen with custom cabinetry, countertops, tile, lighting, and full layout redesigns. We handle everything from demo to final inspection.',
		link: '/services',
	},
	{
		icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 3v4m-4.24-2.76 2.83 2.83M3 12h4m-.76 4.24 2.83-2.83M12 21v-4m4.24 2.76-2.83-2.83M21 12h-4m.76-4.24-2.83 2.83" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
		title: 'Bathroom Renovation',
		desc: 'From quick refreshes to full gut-and-rebuild projects. Walk-in showers, freestanding tubs, vanity upgrades, heated floors, and modern tile work.',
		link: '/services',
	},
	{
		icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 9h18M9 9v12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
		title: 'Flooring & Tile',
		desc: 'Hardwood, luxury vinyl plank, ceramic & porcelain tile, natural stone, and carpet installation and replacement throughout your home.',
		link: '/services',
	},
	{
		icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M4 12H2M22 12h-2M12 4V2M12 22v-2M6.34 6.34 4.93 4.93M19.07 19.07l-1.41-1.41M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
		title: 'Interior & Exterior Painting',
		desc: 'Professional interior and exterior painting using premium paints. Color consultation, surface prep, clean edges, and a spotless finish every time.',
		link: '/services',
	},
	{
		icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="8" height="18" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="3" width="8" height="18" rx="1" stroke="currentColor" strokeWidth="1.5"/><path d="M10 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>,
		title: 'Windows & Doors',
		desc: 'Energy-efficient window replacement, entry doors, patio doors, and sliding glass doors. Improve curb appeal, insulation, and security in one upgrade.',
		link: '/services',
	},
	{
		icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M3 18h18M5 18V9l7-6 7 6v9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 18v-5h4v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
		title: 'Decks & Patios',
		desc: 'Custom wood and composite decks, covered patios, pergolas, and outdoor living spaces designed for Arizona\'s climate and your lifestyle.',
		link: '/services',
	},
];

const STEPS = [
	{ n: '1', title: 'Free Consultation', desc: 'We visit your home, assess the project, and discuss your goals, timeline, and budget — at no cost.' },
	{ n: '2', title: 'Detailed Estimate', desc: 'You receive a written, itemized estimate with material options, labor costs, and projected timeline.' },
	{ n: '3', title: 'Scheduled Build', desc: 'We confirm a start date, coordinate materials, and keep you informed every step of the way.' },
	{ n: '4', title: 'Final Walkthrough', desc: 'We inspect every detail together before calling the job complete. Your satisfaction is guaranteed.' },
];

const CITIES = ['Phoenix', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 'Gilbert', 'Glendale', 'Peoria', 'Surprise', 'Goodyear', 'Avondale', 'Buckeye'];

function Home() {
	return (
		<>
			<Helmet>
				<title>A-Z Home Improvement LLC | Phoenix, AZ Remodeling Contractor</title>
				<meta name="description" content="A-Z Home Improvement LLC is Phoenix's trusted remodeling and renovation contractor. Kitchen, bathroom, flooring, painting, windows, decks. Free estimates." />
				<script type="application/ld+json">{JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'HomeAndConstructionBusiness',
					name: 'A-Z Home Improvement LLC',
					url: 'https://azhomefundingservices.com',
					telephone: '+16023902536',
					email: 'info@azhomefundingservices.com',
					address: {
						'@type': 'PostalAddress',
						streetAddress: '3842 W. McDowell Rd, Suite 110',
						addressLocality: 'Phoenix',
						addressRegion: 'AZ',
						postalCode: '85009',
						addressCountry: 'US',
					},
					areaServed: 'Phoenix Metropolitan Area, AZ',
					description: 'Full-service residential and commercial home remodeling and renovation contractor serving the Phoenix, AZ metro area.',
				})}</script>
			</Helmet>

			{/* Hero */}
			<section className="hero" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=1600&auto=format&fit=crop)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
				<div className="hero-overlay" />
				<div className="container hero-grid" style={{ position: 'relative', zIndex: 2 }}>
					<div className="hero-content fade-up">
						<div className="hero-badge">🏠 Phoenix's Trusted Remodeling Contractor</div>
						<h1>Transform Your Home with Expert Craftsmanship</h1>
						<p>From kitchen and bathroom remodels to full-home renovations — A-Z Home Improvement LLC delivers quality work, on time and on budget, throughout the Phoenix metro area.</p>
						<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
							<Link to="/contact" className="btn" style={{ fontSize: 15, padding: '12px 26px' }}>Get a Free Quote</Link>
							<a href="tel:+16023902536" className="btn-outline" style={{ fontSize: 15, padding: '12px 26px' }}>(602) 390-2536</a>
						</div>
					</div>
					<div className="fade-up" style={{ animationDelay: '120ms' }}>
						<div className="card" style={{ padding: 24 }}>
							<h3 style={{ margin: '0 0 6px' }}>Start Your Project Today</h3>
							<p className="helper" style={{ margin: '0 0 16px' }}>Free in-home consultation and written estimate. No pressure. No obligation.</p>
							<ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', display: 'flex', flexDirection: 'column', gap: 8 }}>
								{['Free in-home estimate', 'Licensed & fully insured', '5-year workmanship warranty', 'Local Phoenix team'].map(t => (
									<li key={t} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600 }}>
										<span style={{ color: '#c45c14', fontWeight: 800 }}>✓</span> {t}
									</li>
								))}
							</ul>
							<Link to="/contact" className="btn" style={{ width: '100%', justifyContent: 'center' }}>Schedule My Free Estimate</Link>
						</div>
					</div>
				</div>
			</section>

			{/* Trust bar */}
			<div className="trustbar">
				<div className="container trustbar-inner">
					{['✓ AZ Licensed & Bonded', '✓ $2M General Liability', '✓ 500+ Projects Completed', '✓ 5-Star Rated', '✓ 5-Year Warranty', '✓ Free Estimates'].map(t => (
						<span key={t} className="pill">{t}</span>
					))}
				</div>
			</div>

			{/* Services */}
			<section className="section">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 32 }}>
						<h2>Home Improvement Services</h2>
						<p className="helper" style={{ maxWidth: 520, margin: '0 auto' }}>We handle every phase of your remodel — from design planning and permits to final cleanup. No subcontracting surprises.</p>
					</div>
					<div className="grid">
						{SERVICES.map((s, i) => (
							<div key={s.title} className="card feature fade-up" style={{ animationDelay: `${i * 50}ms` }}>
								<div className="icon-circle">{s.icon}</div>
								<h3 style={{ margin: '0 0 6px' }}>{s.title}</h3>
								<p className="helper" style={{ margin: 0 }}>{s.desc}</p>
								<Link to={s.link} className="card-link">Learn more →</Link>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Why choose us */}
			<section className="section section-alt">
				<div className="container two-col" style={{ alignItems: 'center', gap: 40 }}>
					<div className="img-frame fade-up">
						<img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=1200&auto=format&fit=crop" alt="Home improvement contractor at work" />
					</div>
					<div className="fade-up" style={{ animationDelay: '100ms' }}>
						<h2>Why Phoenix Homeowners Choose A-Z</h2>
						<p className="helper" style={{ margin: '0 0 20px' }}>We've been serving the Phoenix metro since 2019. Our reputation is built on doing the job right — every single time.</p>
						{[
							['Fully Licensed & Insured', 'AZ ROC License #XXXXX8142. $2M GL insurance and workers\' comp on every job. You\'re always protected.'],
							['Transparent Pricing', 'Detailed written estimates. No surprise charges. We discuss every cost before work begins.'],
							['Single Point of Contact', 'Your dedicated project manager keeps you informed with regular updates — no chasing people down.'],
							['Clean Job Sites', 'We protect your home during construction and do a thorough cleanup at the end of every workday.'],
						].map(([title, desc]) => (
							<div key={title} className="check-item">
								<div className="check-icon">✓</div>
								<div><strong>{title}</strong><p className="helper" style={{ margin: '2px 0 0' }}>{desc}</p></div>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Process */}
			<section className="section section-light">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 28 }}>
						<h2>How It Works</h2>
						<p className="helper" style={{ maxWidth: 480, margin: '0 auto' }}>A simple, transparent process from first call to final walkthrough.</p>
					</div>
					<div className="process">
						{STEPS.map(s => (
							<div key={s.n} className="step fade-up">
								<div className="circle">{s.n}</div>
								<h3 style={{ margin: '0 0 6px' }}>{s.title}</h3>
								<p className="helper" style={{ margin: 0 }}>{s.desc}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Stats */}
			<section className="section section-alt">
				<div className="container">
					<div className="stats">
						{[
							['500+', 'Projects Completed', 'Residential and commercial projects across the Phoenix metro area since 2019.'],
							['5★', 'Average Rating', 'Consistently 5-star reviews on Google, Houzz, and NextDoor from satisfied homeowners.'],
							['5-Year', 'Workmanship Warranty', 'Every installation is backed by our 5-year workmanship guarantee. We stand behind our work.'],
						].map(([val, label, desc]) => (
							<div key={label} className="card stat">
								<strong>{val}</strong>
								<div style={{ fontWeight: 700, margin: '4px 0 6px' }}>{label}</div>
								<p className="helper" style={{ margin: 0 }}>{desc}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Service Areas */}
			<section className="section section-light">
				<div className="container">
					<div style={{ textAlign: 'center', marginBottom: 20 }}>
						<h2>Serving the Phoenix Metro Area</h2>
						<p className="helper" style={{ maxWidth: 460, margin: '0 auto' }}>We serve homeowners and property owners throughout Maricopa County.</p>
					</div>
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 20 }}>
						{CITIES.map(c => (
							<span key={c} className="badge">{c}</span>
						))}
					</div>
					<p className="helper" style={{ textAlign: 'center', marginTop: 12 }}>
						Don't see your city? <Link to="/contact" style={{ color: 'var(--brand)', fontWeight: 600 }}>Contact us</Link> — we likely serve your area.
					</p>
				</div>
			</section>

			{/* CTA */}
			<section className="cta-section">
				<div className="container">
					<h2>Ready to Start Your Remodel?</h2>
					<p>Contact us today for a free in-home consultation and written estimate — no obligation, no pressure.</p>
					<div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
						<Link to="/contact" className="btn" style={{ fontSize: 15, padding: '12px 28px' }}>Get a Free Estimate</Link>
						<a href="tel:+16023902536" className="btn-outline" style={{ fontSize: 15, padding: '12px 28px' }}>Call (602) 390-2536</a>
					</div>
				</div>
			</section>
		</>
	);
}

export default Home;
